import React, { useEffect, useState } from 'react';
import { useQueryParams } from '@app/hooks/useQueryParams';
import { Button, Title, Text, SpinningLoader } from '@nicheinc/campus-ds';
import axios from '@global/axios';
import { apiUrl } from '@app/constants';
import * as S from './Unsubscribe.styles';

type UnsubscribeState =
	| 'fetching'
	| 'confirm'
	| 'loading'
	| 'success'
	| 'error';

function Unsubscribe() {
	const { queryParams } = useQueryParams();
	const token = queryParams.get('token');

	const [state, setState] = useState<UnsubscribeState>('fetching');
	const [email, setEmail] = useState<string | null>(null);
	const [errorMessage, setErrorMessage] = useState(
		'Something went wrong. Please try again or contact support.',
	);

	useEffect(() => {
		if (!token) {
			setErrorMessage('Invalid unsubscribe link.');
			setState('error');
			return;
		}
		axios
			.get(`${apiUrl}/unsubscribe/recipient`, { params: { token } })
			.then((res) => {
				setEmail(res.data.email);
				setState('confirm');
			})
			.catch((err: any) => {
				setErrorMessage(
					err?.response?.data?.message ||
						'This unsubscribe link is invalid or has expired.',
				);
				setState('error');
			});
	}, [token]);

	const handleUnsubscribe = async () => {
		setState('loading');
		try {
			await axios.post(`${apiUrl}/unsubscribe`, { token });
			setState('success');
		} catch (err: any) {
			setErrorMessage(
				err?.response?.data?.message ||
					'Something went wrong. Please try again or contact support.',
			);
			setState('error');
		}
	};

	const renderContent = () => {
		switch (state) {
			case 'fetching':
				return (
					<>
						<S.LoaderContainer>
							<SpinningLoader />
						</S.LoaderContainer>
						<Title as="h2" size="xl">
							Loading...
						</Title>
					</>
				);

			case 'confirm':
				return (
					<>
						<S.IconContainer>
							<S.EmailIcon>✉️</S.EmailIcon>
						</S.IconContainer>
						<Title as="h2" size="xl">
							Unsubscribe from Emails
						</Title>
						<S.Description>
							<Text size="regular">
								Are you sure you want to unsubscribe from this email?
							</Text>
							<S.EmailBadge>
								<Text size="regular">
									<strong>{email}</strong>
								</Text>
							</S.EmailBadge>
						</S.Description>
						<S.ButtonContainer>
							<Button variant="primary" onClick={handleUnsubscribe}>
								Unsubscribe
							</Button>
						</S.ButtonContainer>
					</>
				);

			case 'loading':
				return (
					<>
						<S.LoaderContainer>
							<SpinningLoader />
						</S.LoaderContainer>
						<Title as="h2" size="xl">
							Processing...
						</Title>
						<S.Description>
							<Text size="regular">
								Please wait while we process your request.
							</Text>
						</S.Description>
					</>
				);

			case 'success':
				return (
					<>
						<S.IconContainer>
							<S.SuccessIcon>✓</S.SuccessIcon>
						</S.IconContainer>
						<Title as="h2" size="xl">
							Successfully Unsubscribed
						</Title>
						<S.Description>
							<Text size="regular">
								The following email has been unsubscribed successfully.
							</Text>
							<S.EmailBadge>
								<Text size="regular">
									<strong>{email}</strong>
								</Text>
							</S.EmailBadge>
							<Text size="small">
								You will no longer receive emails from this list.
							</Text>
						</S.Description>
					</>
				);

			case 'error':
				return (
					<>
						<S.IconContainer>
							<S.ErrorIcon>✕</S.ErrorIcon>
						</S.IconContainer>
						<Title as="h2" size="xl">
							Unsubscribe Failed
						</Title>
						<S.Description>
							<Text size="regular" color="error.text.default">
								{errorMessage}
							</Text>
						</S.Description>
					</>
				);

			default:
				return null;
		}
	};

	return (
		<S.Container>
			<S.Card>{renderContent()}</S.Card>
		</S.Container>
	);
}

export default Unsubscribe;
